import { useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from './cn.js';

/// Controle deslizante com trilho e alça do tema. Não é o `input type="range"` do navegador, que
/// muda de desenho a cada plataforma: arrastar, clicar no trilho e as setas do teclado ajustam.
export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  valueText,
  onChange,
  disabled,
  className,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  /// O valor como a pessoa lê, anunciado pelo leitor de tela (por exemplo, "150%").
  valueText?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
}) {
  const track = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const fit = (raw: number) =>
    Math.min(max, Math.max(min, Math.round((raw - min) / step) * step + min));
  function at(clientX: number) {
    const box = track.current!.getBoundingClientRect();
    if (!box.width) return;
    const next = fit(min + ((clientX - box.left) / box.width) * (max - min));
    if (next !== value) onChange(next);
  }
  function key(event: KeyboardEvent<HTMLDivElement>) {
    const big = Math.max(step, (max - min) / 10);
    const next = {
      ArrowLeft: value - step,
      ArrowDown: value - step,
      ArrowRight: value + step,
      ArrowUp: value + step,
      PageDown: value - big,
      PageUp: value + big,
      Home: min,
      End: max,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const fitted = fit(next);
    if (fitted !== value) onChange(fitted);
  }
  const percent = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <div
      ref={track}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={valueText}
      aria-disabled={disabled || undefined}
      className={cn(
        'group relative flex h-6 w-full cursor-pointer touch-none select-none items-center rounded-full focus-visible:outline-none aria-disabled:cursor-not-allowed aria-disabled:opacity-60',
        className,
      )}
      onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
        if (disabled) return;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        dragging.current = true;
        at(event.clientX);
      }}
      onPointerMove={(event) => {
        if (dragging.current) at(event.clientX);
      }}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
      onKeyDown={disabled ? undefined : key}
    >
      <span aria-hidden="true" className="absolute inset-x-0 h-1.5 rounded-full bg-content/15">
        <span
          className="absolute inset-y-0 left-0 rounded-full bg-brand-500"
          style={{ width: `${percent}%` }}
        />
      </span>
      <span
        aria-hidden="true"
        className="absolute size-4 -translate-x-1/2 rounded-full border-2 border-brand-500 bg-surface-card shadow transition-shadow group-focus-visible:ring-2 group-focus-visible:ring-brand-500/50"
        style={{ left: `${percent}%` }}
      />
    </div>
  );
}
